import React, { useState } from 'react';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import './AuthModal.css';

const schema = yup.object({
  businessName: yup.string().required('Business name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  contactName: yup.string().required('Contact name is required'),
  phone: yup.string().required('Phone number is required'),
  businessAddress: yup.string().required('Business address is required'),
  city: yup.string().required('City is required'),
  venueType: yup.string().required('Please select a venue type'),
  maxCapacity: yup.number().min(1, 'Capacity must be at least 1').required('Maximum capacity is required'),
  hourlyRate: yup.number().min(0, 'Rate must be 0 or higher').required('Hourly rate is required')
});

const VenueSignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { signup, error, clearError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema)
  });

  const handleClose = () => {
    clearError();
    reset();
    setCurrentStep(1);
    onClose();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Create Firebase account
      await signup(data.email, data.password, data.contactName, {
        user_type: 'venue_owner',
        business_name: data.businessName,
        phone: data.phone,
        business_address: data.businessAddress,
        city: data.city,
        venue_type: data.venueType,
        max_capacity: data.maxCapacity,
        hourly_rate: data.hourlyRate
      });
      
      handleClose();
      // TODO: Show success message and redirect to venue setup
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(2);
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  useCloseOnEscape(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal venue-signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="auth-modal-content">
          <div className="auth-modal-header">
            <h2 className="auth-modal-title">Register Your Venue</h2>
            <p className="auth-modal-subtitle">
              Join our platform and start accepting bookings today
            </p>
          </div>

          <div className="auth-modal-body">
            {/* Progress Indicator */}
            <div className="signup-progress">
              <div className="progress-step">
                <div className={`step-number ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                <span className="step-label">Account Details</span>
              </div>
              <div className="progress-line"></div>
              <div className="progress-step">
                <div className={`step-number ${currentStep >= 2 ? 'active' : ''}`}>2</div>
                <span className="step-label">Venue Information</span>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
              {currentStep === 1 && (
                <>
                  {/* Account Details */}
                  <div className="auth-form-group">
                    <input
                      type="text"
                      placeholder="Business Name *"
                      className={`auth-input ${errors.businessName ? 'auth-input-error' : ''}`}
                      {...register('businessName')}
                    />
                    {errors.businessName && (
                      <span className="auth-error-message">{errors.businessName.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <input
                      type="email"
                      placeholder="Business Email *"
                      className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                      {...register('email')}
                    />
                    {errors.email && (
                      <span className="auth-error-message">{errors.email.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <input
                      type="password"
                      placeholder="Password *"
                      className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                      {...register('password')}
                    />
                    {errors.password && (
                      <span className="auth-error-message">{errors.password.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <input
                      type="text"
                      placeholder="Contact Name *"
                      className={`auth-input ${errors.contactName ? 'auth-input-error' : ''}`}
                      {...register('contactName')}
                    />
                    {errors.contactName && (
                      <span className="auth-error-message">{errors.contactName.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <input
                      type="tel"
                      placeholder="Phone Number *"
                      className={`auth-input ${errors.phone ? 'auth-input-error' : ''}`}
                      {...register('phone')}
                    />
                    {errors.phone && (
                      <span className="auth-error-message">{errors.phone.message}</span>
                    )}
                  </div>

                  <button 
                    type="button" 
                    className="auth-submit-btn"
                    onClick={nextStep}
                  >
                    Next Step
                  </button>
                </>
              )}

              {currentStep === 2 && (
                <>
                  {/* Venue Information */}
                  <div className="auth-form-group">
                    <input
                      type="text"
                      placeholder="Business Address *"
                      className={`auth-input ${errors.businessAddress ? 'auth-input-error' : ''}`}
                      {...register('businessAddress')}
                    />
                    {errors.businessAddress && (
                      <span className="auth-error-message">{errors.businessAddress.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <input
                      type="text"
                      placeholder="City *"
                      className={`auth-input ${errors.city ? 'auth-input-error' : ''}`}
                      {...register('city')}
                    />
                    {errors.city && (
                      <span className="auth-error-message">{errors.city.message}</span>
                    )}
                  </div>

                  <div className="auth-form-group">
                    <select
                      className={`auth-input ${errors.venueType ? 'auth-input-error' : ''}`}
                      {...register('venueType')}
                    >
                      <option value="">Select Venue Type *</option>
                      <option value="conference_room">Conference Room</option>
                      <option value="banquet_hall">Banquet Hall</option>
                      <option value="outdoor_space">Outdoor Space</option>
                      <option value="studio">Studio</option>
                      <option value="loft">Loft</option>
                      <option value="hotel">Hotel</option>
                      <option value="cafe">Café</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="auditorium">Auditorium</option>
                    </select>
                    {errors.venueType && (
                      <span className="auth-error-message">{errors.venueType.message}</span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="auth-form-group">
                      <input
                        type="number"
                        placeholder="Max Capacity *"
                        className={`auth-input ${errors.maxCapacity ? 'auth-input-error' : ''}`}
                        {...register('maxCapacity')}
                      />
                      {errors.maxCapacity && (
                        <span className="auth-error-message">{errors.maxCapacity.message}</span>
                      )}
                    </div>

                    <div className="auth-form-group">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Hourly Rate (£) *"
                        className={`auth-input ${errors.hourlyRate ? 'auth-input-error' : ''}`}
                        {...register('hourlyRate')}
                      />
                      {errors.hourlyRate && (
                        <span className="auth-error-message">{errors.hourlyRate.message}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="auth-submit-btn auth-submit-btn-secondary"
                      onClick={prevStep}
                    >
                      Back
                    </button>
                    <button 
                      type="submit" 
                      className="auth-submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Creating Account...' : 'Register Venue'}
                    </button>
                  </div>
                </>
              )}

              {error && (
                <div className="auth-error-message auth-error-general">
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="auth-modal-footer">
            <p className="auth-switch-text">
              Already have a venue account?{' '}
              <button 
                type="button" 
                className="auth-switch-link"
                onClick={onSwitchToLogin}
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueSignupModal;
